/**
 * Профиль пользователя (ТЗ §8.8): сведения об учётной записи, смена пароля,
 * настройки уведомлений и привязка MAX.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';

import { useMaxLink, useUpdateMe } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { CheckBox } from '../components/Badges';
import { FormError, LoadingState, errorMessage } from '../components/ErrorState';
import { useToast } from '../components/Toast';
import { fmtDate } from '../lib/format';
import { ROLE_LABELS } from '../lib/stages';
import type { NotifyPrefs } from '../api/types';

const NOTIFY_LABELS: Array<{ key: keyof NotifyPrefs; label: string }> = [
    { key: 'assign', label: 'Назначение и переназначение дела' },
    { key: 'deadline', label: 'Приближение срока и просрочка' },
    { key: 'approval', label: 'Согласование ответа' },
    { key: 'escalation', label: 'Эскалация и продление срока' },
];

export default function ProfilePage() {
    const { me, user, clinic, maxLinkCode } = useAuth();
    const toast = useToast();
    const updateMe = useUpdateMe();
    const maxLink = useMaxLink();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [code, setCode] = useState<string | null>(null);

    if (!me || !user) return <LoadingState label="Загружаем профиль…" />;

    const prefs = me.user.notify_prefs;

    const submitPassword = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!currentPassword || !newPassword) {
            setPasswordError('Заполните текущий и новый пароль');
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError('Новый пароль короче 8 знаков');
            return;
        }
        if (newPassword !== repeatPassword) {
            setPasswordError('Новый пароль и повтор не совпадают');
            return;
        }
        setPasswordError('');
        updateMe.mutate(
            { current_password: currentPassword, new_password: newPassword },
            {
                onSuccess: () => {
                    setCurrentPassword('');
                    setNewPassword('');
                    setRepeatPassword('');
                    toast('Пароль изменён');
                },
                onError: (error) => setPasswordError(errorMessage(error)),
            },
        );
    };

    const togglePref = (key: keyof NotifyPrefs, next: boolean) => {
        const nextPrefs: NotifyPrefs = { ...prefs };
        nextPrefs[key] = next;
        updateMe.mutate(
            { notify_prefs: nextPrefs },
            { onSuccess: () => toast('Настройки уведомлений сохранены') },
        );
    };

    const requestCode = () => {
        maxLink.mutate(undefined, {
            onSuccess: (data) => {
                setCode(data.code);
                toast('Код привязки создан');
            },
            onError: (error) => toast(errorMessage(error)),
        });
    };

    return (
        <>
            <div className="page-header">
                <h1>
                    👤 Профиль<span>{clinic?.name ?? ''}</span>
                </h1>
            </div>

            <div className="two-col">
                <div className="col col-left">
                    <div className="stat-block">
                        <div className="block-header">Учётная запись</div>
                        <div className="block-body">
                            <div className="kv">
                                <div className="k">ФИО</div>
                                <div className="v">{user.full_name}</div>
                                <div className="k">Логин</div>
                                <div className="v">{user.login}</div>
                                <div className="k">Роль</div>
                                <div className="v">{ROLE_LABELS[user.role]}</div>
                                <div className="k">Должность</div>
                                <div className="v">{user.post ?? '—'}</div>
                                <div className="k">Отделение</div>
                                <div className="v">{user.department_name ?? '—'}</div>
                                <div className="k">Клиника</div>
                                <div className="v">{clinic?.name ?? '—'}</div>
                                <div className="k">Учётная запись создана</div>
                                <div className="v">{fmtDate(user.created_at)}</div>
                                <div className="k">Уведомления в MAX</div>
                                <div className={user.max_linked ? 'v green' : 'v red'}>
                                    {user.max_linked ? 'привязаны' : 'не привязаны'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col col-right">
                    <div className="stat-block">
                        <div className="block-header">Смена пароля</div>
                        <div className="block-body">
                            <form className="form-grid" onSubmit={submitPassword}>
                                <label className="form-field">
                                    <span>Текущий пароль</span>
                                    <input
                                        className="form-input"
                                        type="password"
                                        autoComplete="current-password"
                                        value={currentPassword}
                                        onChange={(event) => setCurrentPassword(event.target.value)}
                                    />
                                </label>
                                <label className="form-field">
                                    <span>Новый пароль</span>
                                    <input
                                        className="form-input"
                                        type="password"
                                        autoComplete="new-password"
                                        value={newPassword}
                                        onChange={(event) => setNewPassword(event.target.value)}
                                    />
                                </label>
                                <label className="form-field">
                                    <span>Повтор нового пароля</span>
                                    <input
                                        className="form-input"
                                        type="password"
                                        autoComplete="new-password"
                                        value={repeatPassword}
                                        onChange={(event) => setRepeatPassword(event.target.value)}
                                    />
                                </label>
                                <FormError>{passwordError}</FormError>
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="act-btn primary"
                                        disabled={updateMe.isPending}
                                    >
                                        Сменить пароль
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <div className="stat-block">
                <div className="block-header">Уведомления</div>
                <div className="block-body">
                    {NOTIFY_LABELS.map((item) => (
                        <div className="check-item" key={String(item.key)}>
                            <CheckBox
                                checked={Boolean(prefs[item.key])}
                                disabled={updateMe.isPending}
                                onChange={(next) => togglePref(item.key, next)}
                            />
                            <span className="check-text">{item.label}</span>
                        </div>
                    ))}

                    <div className="draft-bar">
                        <button
                            type="button"
                            className="act-btn"
                            onClick={requestCode}
                            disabled={maxLink.isPending}
                        >
                            {user.max_linked ? 'Перепривязать MAX' : 'Привязать MAX'}
                        </button>
                        <span className="draft-note">
                            Уведомления приходят в мессенджер MAX. Без привязки они не отправляются.
                        </span>
                    </div>

                    {(code ?? maxLinkCode) && (
                        <div className="modal-note">
                            Отправьте боту код <b>{code ?? maxLinkCode}</b> — после этого уведомления
                            начнут приходить в MAX.
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
